import { Component, h, Prop, Host, Element, Watch, State, EventEmitter, Event } from '@stencil/core';
import { IconSize } from './sew-icon.types';
import { iconCacheService } from './sew-icon-cache.service';

/**
 * SewOpenSvgIcon
 * Renders a svg icon without shadow DOM for easier manipulation from the outside, e.g. for animations.
 * The icon is specified by the icon input and is taken from the ./icons.ts file.
 */
@Component({
    tag: 'sew-icon',
    styleUrl: 'sew-icon.scss',
    shadow: false,
})
export class SewIcon {
    @Element() host: HTMLSewIconElement;

    @Event() iconLoaded: EventEmitter<boolean>;

    /**
     * declared icon names can be found in Storybook
     * also accepts url to an external svg
     */
    @Prop({ reflect: true }) icon: string;

    /**
     * default sets width according to viewbox
     * also possible to set a width in px for example: 24
     */
    @Prop() width?: string;

    /**
     * set a height in px for example: 24
     * if height not set, "auto" will be used in css to calculate height
     */
    @Prop() height?: string;

    /**
     * set fluid to true to make the icon 100% width
     */
    @Prop() fluid = false;

    /**
     * alias for the size
     * s = 20px
     * m = 24px (default)
     * l = 32px
     * xl = 48px
     */
    @Prop() size?: IconSize = 'm';

    @Prop() disableCache = false;

    @State() error = false;

    private sizeMapping = {
        s: '20',
        m: '24',
        l: '32',
        xl: '48',
    };

    @Watch('width')
    setWidth() {
        let width = this.width;

        if (this.fluid) {
            width = '100%';
        } else if (!this.width) {
            width = String(this.host.querySelector('svg')?.viewBox.baseVal.width);
        }

        this.host.style.setProperty('--svg-width', width + 'px');
    }

    @Watch('height')
    setHeight() {
        if (this.height && !this.fluid) {
            this.host.style.setProperty('--svg-height', this.height + 'px');
        }
    }

    @Watch('size')
    setSize() {
        this.width = this.sizeMapping[this.size];
        this.height = this.sizeMapping[this.size];
    }

    @Watch('icon')
    async loadSvg() {
        iconCacheService
            .getIcon(this.icon, this.size, this.disableCache)
            .then(svg => {
                this.host.innerHTML = svg;
                this.setWidth();
                this.setHeight();
                this.iconLoaded.emit(true);
            })
            .catch(e => {
                this.error = true;
                console.warn(e);
            });
    }

    componentWillLoad() {
        this.loadSvg();
    }

    public render() {
        return <Host class={{ invisible: this.error }}></Host>;
    }
}
